import { useRef, useState } from 'react';

import {
  usePlatformUpdateFilterTeam,
  usePlatformTableListFiltersTeam,
} from '@cloudquery/api-platform';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import {
  SearchField,
  CopyToClipboardButton,
  DetailsDrawer,
  usePagination,
  CLOUD_ASSET_TABLE_NAME,
  TableCard,
  ApiDataWrapper,
  trackEvent,
  CloseButton,
} from 'shared/base';

import { SavedFiltersPanelActionsMenu } from './actionsMenu';
import { useTableStateContext } from '../../../hooks/useTableStateContext';
import { DataFilterString } from '../../../types';
import { displayFilterString, displayFilterNodes } from '../../../utils/tableState';

interface Props {
  enableWriteActions: boolean;
  onClose: () => void;
  onSaveCurrentFilter: () => void;
  open: boolean;
  teamName: string;
}

export function SavedFiltersPanel({
  enableWriteActions,
  onClose,
  onSaveCurrentFilter,
  open,
  teamName,
}: Props) {
  const {
    applySavedFilter,
    filter: currentFilter,
    originalFilter,
    savedFilterHasDeviated,
  } = useTableStateContext();
  const [shownFilterPreviewId, setShownFilterPreviewId] = useState<string | null>(null);
  const filterPreviewRefs = useRef<Record<string, HTMLDivElement>>({});
  const [shownFilterMenuId, setShownFilterMenuId] = useState<string | null>(null);
  const filterMenuRefs = useRef<Record<string, HTMLButtonElement>>({});
  const [search, setSearch] = useState('');
  const { perPage, setPerPage, setTablePage, tablePage } = usePagination({
    tableId: 'asset-inventory-saved-filters',
  });
  const { mutateAsync: updateFilter } = usePlatformUpdateFilterTeam();
  const {
    data: filtersData,
    isError: filtersError,
    isLoading: filtersLoading,
  } = usePlatformTableListFiltersTeam(
    teamName,
    CLOUD_ASSET_TABLE_NAME,
    {
      page: 1,
      per_page: 1000,
    },
    {
      query: {
        enabled: open,
      },
    },
  );

  const filteredQueries =
    filtersData?.data?.items.filter(
      (filter) =>
        filter.name.toLowerCase().includes(search.toLowerCase()) ||
        filter.expression.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const filteredQueriesPage = filteredQueries.slice(tablePage * perPage, (tablePage + 1) * perPage);
  const filtersCount = filteredQueries.length;

  const onSelect = (filterId: string, filterExpression: string) => {
    onClose();

    trackEvent('inventory_filter_used');
    applySavedFilter(filterId, filterExpression);
  };

  const onSaveExistingFilter = () => {
    if (!originalFilter) {
      return;
    }

    updateFilter({
      data: {
        expression: currentFilter,
        name: originalFilter.name,
      },
      filterId: originalFilter.id,
      teamName,
    })
      .then(onClose)
      .catch(() => null);
  };

  return (
    <DetailsDrawer
      anchor="right"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: { maxWidth: '100%', width: '900px' },
        },
      }}
    >
      <Stack spacing={3}>
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Typography variant="h5">Saved filters</Typography>
          <CloseButton onClose={onClose} />
        </Stack>
        <Stack gap={4}>
          {enableWriteActions && currentFilter.length > 0 && (
            <Stack gap={2}>
              <Stack gap={1}>
                <Typography variant="h6">Current filter</Typography>
                <Stack direction="row" gap={1}>
                  <Stack
                    borderRadius={1}
                    direction="row"
                    flexGrow={1}
                    gap={1}
                    padding={1}
                    sx={{ backgroundColor: 'background.paperTertiary' }}
                  >
                    <Typography variant="body2Bold">
                      {originalFilter?.name ?? 'Unsaved view'}
                    </Typography>
                    <Typography variant="body2">{displayFilterString(currentFilter)}</Typography>
                  </Stack>
                  <Button
                    color="primary"
                    endIcon={<AddIcon />}
                    onClick={() =>
                      savedFilterHasDeviated ? onSaveExistingFilter() : onSaveCurrentFilter()
                    }
                    variant="contained"
                  >
                    Save this filter
                  </Button>
                  {savedFilterHasDeviated && (
                    <Button color="primary" onClick={onSaveCurrentFilter} variant="outlined">
                      Save as new filter
                    </Button>
                  )}
                </Stack>
              </Stack>
              {savedFilterHasDeviated && (
                <Alert
                  severity="info"
                  sx={(theme) => ({
                    backgroundColor: theme.palette.info.light,
                    color: theme.palette.info.dark,
                  })}
                >
                  This filter has unsaved changes. Save changes in this filter or create a save as
                  new filter.
                </Alert>
              )}
            </Stack>
          )}
          <Stack gap={1}>
            <Typography variant="h6">Saved & shared filters</Typography>
            <SearchField
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch('')}
              placeholder="Search by filters name, expression or tags"
              sx={{ height: '40px' }}
              value={search}
              variant="outlined"
            />
            <ApiDataWrapper
              data={filteredQueriesPage}
              emptyTitle="No filters found"
              errorBoxProps={{
                attemptedAction: 'load filters',
              }}
              isEmpty={filteredQueriesPage.length === 0}
              isError={filtersError}
              isLoading={filtersLoading}
            >
              {(data) => (
                <TableCard
                  count={filtersCount}
                  onPageChange={setTablePage}
                  onPerPageChange={setPerPage}
                  page={tablePage}
                  perPage={perPage}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Filter name</TableCell>
                        <TableCell>Properties</TableCell>
                        {/* <TableCell>Created By</TableCell>
                        <TableCell>Tags</TableCell> */}
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((filter) => {
                        const previewAnchorEl = filterPreviewRefs.current[filter.id];
                        const menuAnchorEl = filterMenuRefs.current[filter.id];

                        return (
                          <TableRow
                            key={filter.id}
                            sx={{
                              '&:hover': {
                                '.asset-inventory-filters-panel-select-filter-button': {
                                  opacity: 1,
                                },
                              },
                            }}
                          >
                            <TableCell>{filter.name}</TableCell>
                            <TableCell>
                              <Stack
                                ref={(el: HTMLDivElement) => {
                                  filterPreviewRefs.current[filter.id] = el;
                                }}
                                alignItems="center"
                                bgcolor="secondary.hovered"
                                borderRadius={0.5}
                                direction="row"
                                paddingX={1}
                                spacing={1}
                                sx={{
                                  '&:hover': {
                                    '.asset-inventory-filters-panel-preview-button': { opacity: 1 },
                                    bgcolor: 'secondary.dark',
                                  },
                                }}
                              >
                                <Box maxWidth={300} overflow="hidden" textOverflow="ellipsis">
                                  {displayFilterString(filter.expression as DataFilterString)}
                                </Box>

                                <IconButton
                                  className="asset-inventory-filters-panel-preview-button"
                                  onClick={() => setShownFilterPreviewId(filter.id)}
                                  size="small"
                                  sx={{
                                    flexShrink: 0,
                                    height: 20,
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    width: 20,
                                  }}
                                >
                                  <OpenInFullIcon sx={{ fontSize: 16, height: 16, width: 16 }} />
                                </IconButton>
                              </Stack>
                              <Popover
                                anchorEl={previewAnchorEl}
                                anchorOrigin={{
                                  horizontal: 'left',
                                  vertical: 'top',
                                }}
                                id={filter.id}
                                onClose={() => setShownFilterPreviewId(null)}
                                open={shownFilterPreviewId === filter.id}
                              >
                                <Stack
                                  bgcolor="background.paper"
                                  maxHeight={380}
                                  paddingY={1}
                                  position="relative"
                                >
                                  <Box flex="1 1 auto" minHeight={0} overflow="auto" paddingX={2}>
                                    {displayFilterNodes(
                                      filter.expression as string as DataFilterString,
                                    ).map((node) => (
                                      <div key={node}>{node}</div>
                                    ))}
                                  </Box>
                                  <Stack
                                    bgcolor="background.paper"
                                    direction="row"
                                    justifyContent="flex-end"
                                    position="absolute"
                                    right={0}
                                    top={3}
                                  >
                                    <CopyToClipboardButton
                                      size="small"
                                      text={displayFilterString(
                                        filter.expression as string as DataFilterString,
                                      )}
                                    />
                                    <IconButton
                                      onClick={() => setShownFilterPreviewId(null)}
                                      size="small"
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </Popover>
                            </TableCell>
                            {/* <TableCell>{(filter as any).created_by}</TableCell>
                            <TableCell>
                              <TagsDisplay tags={(filter as any).tags} />
                            </TableCell> */}
                            <TableCell align="right">
                              <Button
                                className="asset-inventory-filters-panel-select-filter-button"
                                onClick={() => onSelect(filter.id, filter.expression)}
                                size="small"
                                sx={{
                                  bgcolor: 'neutral.300',
                                  mr: 1,
                                  opacity: 0,
                                  transition: 'opacity 0.2s',
                                }}
                                variant="contained"
                              >
                                Apply filter
                              </Button>
                              {enableWriteActions && (
                                <>
                                  <IconButton
                                    ref={(el: HTMLButtonElement) => {
                                      filterMenuRefs.current[filter.id] = el;
                                    }}
                                    aria-expanded={
                                      shownFilterMenuId === filter.id ? 'true' : undefined
                                    }
                                    aria-haspopup="true"
                                    onClick={() => setShownFilterMenuId(filter.id)}
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                  <SavedFiltersPanelActionsMenu
                                    anchorEl={menuAnchorEl}
                                    filter={filter}
                                    menuId={filter.id}
                                    menuToggleId={filter.id}
                                    onClose={() => setShownFilterMenuId(null)}
                                    open={shownFilterMenuId === filter.id}
                                    teamName={teamName}
                                  />
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableCard>
              )}
            </ApiDataWrapper>
          </Stack>
        </Stack>
      </Stack>
    </DetailsDrawer>
  );
}
